/**
 * QuoteFinanceStrip — top-of-page finance + readiness snapshot (Quote Ops v1).
 */

import React, { useMemo } from "react";
import { Box, Card, Chip, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  formatQuoteMoney,
  getQuoteInternalSnapshot,
} from "../services/quoteOperationalStatus.js";
import {
  buildQuoteReadinessChecklist,
  summarizeReadinessChecklist,
} from "../utils/quoteReadinessChecklist.js";

const Strip = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  overflow: "hidden",
}));

const Metric = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: 10,
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
  height: "100%",
}));

const QuoteFinanceStrip = ({ quote, review = null }) => {
  const snap = useMemo(() => getQuoteInternalSnapshot(quote), [quote]);
  const checklist = useMemo(
    () => buildQuoteReadinessChecklist(quote, review),
    [quote, review]
  );
  const readiness = useMemo(
    () => summarizeReadinessChecklist(checklist),
    [checklist]
  );

  if (!quote) return null;

  return (
    <Strip elevation={0} data-testid="quote-finance-strip">
      <Box sx={{ p: 2 }}>
        <Grid container spacing={1.5} alignItems="stretch">
          <Grid item xs={6} sm={3}>
            <Metric>
              <Typography variant="caption" color="text.secondary">
                Customer price
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {formatQuoteMoney(snap.customerTotal)}
              </Typography>
              {snap.deposit > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Deposit {formatQuoteMoney(snap.deposit)}
                  {snap.depositPercent != null ? ` (${snap.depositPercent}%)` : ""}
                </Typography>
              )}
            </Metric>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Metric>
              <Typography variant="caption" color="text.secondary">
                Provider cost
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {snap.expectedProviderCost != null
                  ? formatQuoteMoney(snap.expectedProviderCost)
                  : "—"}
              </Typography>
            </Metric>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Metric>
              <Typography variant="caption" color="text.secondary">
                Bubakii revenue
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: "success.dark" }}
              >
                {snap.revenue != null ? formatQuoteMoney(snap.revenue) : "—"}
              </Typography>
            </Metric>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Metric>
              <Typography variant="caption" color="text.secondary">
                Margin
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: "primary.main" }}
              >
                {snap.marginPercent != null ? `${snap.marginPercent}%` : "—"}
              </Typography>
            </Metric>
          </Grid>
        </Grid>

        <Box
          mt={1.5}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Quote readiness
            </Typography>
            <Chip
              size="small"
              color={readiness.tone === "success" ? "success" : readiness.tone === "warning" ? "warning" : "default"}
              label={readiness.headline}
              data-testid="quote-readiness-headline"
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {readiness.ready
              ? "All checks passed — expand preparation details only if needed."
              : "Expand preparation details below to resolve blockers."}
          </Typography>
        </Box>
      </Box>
    </Strip>
  );
};

export default QuoteFinanceStrip;
